// Analytics tracking for pricing conversions
export interface PricingAnalytics {
  eventType:
    'tier_selection' | 'contact_sales' | 'schedule_demo' | 'get_started' | 'add_on_selection';
  tier?: 'silver' | 'gold' | 'custom';
  addOnId?: string;
  addOnName?: string;
  selected?: boolean;
  source: string;
  timestamp: string;
  userAgent: string;
  sessionId: string;
}

interface GTAGWindow {
  gtag?: (
    command: string,
    action: string,
    parameters: { [key: string]: string | undefined },
  ) => void;
}

class PricingAnalyticsService {
  private readonly sessionId: string;

  constructor() {
    this.sessionId = this.generateSessionId();
  }

  /**
   * Track pricing button clicks
   */
  trackTierSelection(tier: 'silver' | 'gold' | 'custom', source: string = 'montana_pricing_page') {
    const event: PricingAnalytics = {
      eventType: 'tier_selection',
      tier,
      source,
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      sessionId: this.sessionId,
    };

    this.sendAnalytics(event);
    this.trackGoogleAnalytics('select_tier', { tier, source });
  }

  /**
   * Track contact sales clicks
   */
  trackContactSales(source: string = 'montana_pricing_page') {
    const event: PricingAnalytics = {
      eventType: 'contact_sales',
      source,
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      sessionId: this.sessionId,
    };

    this.sendAnalytics(event);
    this.trackGoogleAnalytics('contact_sales', { source });
  }

  /**
   * Track demo scheduling
   */
  trackScheduleDemo(source: string = 'montana_pricing_page') {
    const event: PricingAnalytics = {
      eventType: 'schedule_demo',
      source,
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      sessionId: this.sessionId,
    };

    this.sendAnalytics(event);
    this.trackGoogleAnalytics('schedule_demo', { source });
  }

  /**
   * Track add-on selections
   */
  trackAddOnSelection(
    addOnId: string,
    addOnName: string,
    selected: boolean,
    source: string = 'montana_pricing_page',
  ) {
    const event = {
      eventType: 'add_on_selection' as const,
      addOnId,
      addOnName,
      selected,
      source,
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      sessionId: this.sessionId,
    };

    this.sendAnalytics(event);
    this.trackGoogleAnalytics('select_addon', {
      addOnId,
      addOnName,
      selected: selected.toString(),
      source,
    });
  }

  /**
   * Send analytics to our backend
   */
  private sendAnalytics(event: PricingAnalytics) {
    // Store locally for now - will integrate with backend analytics
    const events = JSON.parse(localStorage.getItem('pricing_analytics') || '[]');
    events.push(event);
    localStorage.setItem('pricing_analytics', JSON.stringify(events));

    console.log('Pricing analytics tracked:', event);
  }

  /**
   * Send to Google Analytics if available
   */
  private trackGoogleAnalytics(
    eventName: string,
    parameters: { [key: string]: string | undefined },
  ) {
    if (typeof window !== 'undefined' && (window as unknown as GTAGWindow).gtag) {
      (window as unknown as GTAGWindow).gtag?.('event', eventName, {
        event_category: 'conversion',
        event_label: parameters.source || 'unknown',
        custom_parameter_tier: parameters.tier,
        ...parameters,
      });
    }
  }

  /**
   * Generate unique session ID
   */
  private generateSessionId(): string {
    return `sess_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Get analytics summary for admin dashboard
   */
  getAnalyticsSummary(): PricingAnalytics[] {
    return JSON.parse(localStorage.getItem('pricing_analytics') || '[]');
  }
}

export const pricingAnalytics = new PricingAnalyticsService();
